import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Icon, Label, Popup, Button } from 'semantic-ui-react';
import MessageCenter from './MessageCenter/MessageCenter';
import {
  fetchUserConversations,
  selectUnreadCount,
  selectConversations
} from '../../slices/messageSlice';
import { selectCurrentUser } from '../../slices/userSlice';
import { useUniversalMessaging } from './hooks/useUniversalMessaging';
import './UniversalMessageIcon.css';

/**
 * Universal Message Icon Component
 * Displays a floating message icon with unread count badge
 * Integrates with business message types, notifications, and security framework
 */
const UniversalMessageIcon = ({
  position = "top-right",
  showBusinessNotifications = true,
  customMessageStyles = {},
  userRole = null,
  className = "",
  style = {}
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const conversations = useSelector(selectConversations);
  const [isMessageCenterOpen, setIsMessageCenterOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Custom hook for universal messaging functionality
  const {
    totalUnreadCount,
    businessUnreadCount,
    notificationUnreadCount,
    combinedUnreadCount,
    isConnected,
    connectionStatus,
    hasNewMessages,
    businessMessageTypes
  } = useUniversalMessaging(currentUser?.userId, userRole);

  // Show icon only for authenticated users or when explicitly enabled for guests
  useEffect(() => {
    const shouldShow = currentUser?.userId || (userRole === 'guest' && showBusinessNotifications);
    console.log('[MSG_DEBUG] UniversalMessageIcon visibility check:', {
      userId: currentUser?.userId,
      userRole,
      primaryRole: currentUser?.primaryRole,
      shouldShow: !!shouldShow,
      showBusinessNotifications
    });
    setIsVisible(shouldShow);
  }, [currentUser, userRole, showBusinessNotifications]);

  // Fetch user conversations on mount
  useEffect(() => {
    console.log('[MSG_DEBUG] UniversalMessageIcon fetch check:', {
      userId: currentUser?.userId,
      isVisible,
      willFetch: !!(currentUser?.userId && isVisible)
    });
    if (currentUser?.userId && isVisible) {
      console.log('[MSG_DEBUG] Dispatching fetchUserConversations for userId:', currentUser.userId);
      dispatch(fetchUserConversations(currentUser.userId));
    }
  }, [dispatch, currentUser?.userId, isVisible]);

  // Handle message icon click
  const handleIconClick = () => {
    setIsMessageCenterOpen(true);
  };

  // Handle message center close
  const handleMessageCenterClose = () => {
    setIsMessageCenterOpen(false);
  };

  // Get icon style based on message types and connection status
  const getIconStyle = () => {
    const baseStyle = {
      cursor: 'pointer',
      fontSize: '1.5em',
      color: hasNewMessages ? '#2185d0' : '#767676',
      transition: 'all 0.3s ease',
      ...style
    };

    // Notification-specific styling (orange for system notifications)
    if (notificationUnreadCount > 0) {
      baseStyle.color = '#f2711c';
    }

    // Add business-specific styling
    if (businessUnreadCount > 0) {
      baseStyle.color = '#21ba45'; // Green for business messages
    }

    // Connection status styling
    if (!isConnected) {
      baseStyle.opacity = 0.6;
    }

    return baseStyle;
  };

  // Get badge color based on message types
  const getBadgeColor = () => {
    if (notificationUnreadCount > 0) {
      return 'orange'; // System notifications priority
    }
    if (businessUnreadCount > 0) {
      return 'green'; // Business messages priority
    }
    if (totalUnreadCount > 0) {
      return 'blue'; // Regular messages
    }
    return 'grey';
  };

  // Generate position classes
  const getPositionClasses = () => {
    const positions = {
      'top-right': 'universal-message-icon--top-right',
      'top-left': 'universal-message-icon--top-left',
      'bottom-right': 'universal-message-icon--bottom-right',
      'bottom-left': 'universal-message-icon--bottom-left',
      'inline': 'universal-message-icon--inline'
    };
    return positions[position] || positions['top-right'];
  };

  // Create popup content with message breakdown
  const getPopupContent = () => (
    <div className="message-icon-popup">
      <div className="popup-header">
        <Icon name="comments" />
        <strong>Messages & Notifications</strong>
      </div>
      <div className="popup-content">
        {combinedUnreadCount > 0 ? (
          <>
            {/* Message counts */}
            {totalUnreadCount > 0 && (
              <div className="message-count-item">
                <Icon name="mail" color="blue" />
                <span>{totalUnreadCount} unread message{totalUnreadCount !== 1 ? 's' : ''}</span>
              </div>
            )}
            {businessUnreadCount > 0 && (
              <div className="message-count-item">
                <Icon name="briefcase" color="green" />
                <span>{businessUnreadCount} business message{businessUnreadCount !== 1 ? 's' : ''}</span>
              </div>
            )}
            {businessMessageTypes.length > 0 && (
              <div className="business-message-types">
                <small>Types: {businessMessageTypes.join(', ')}</small>
              </div>
            )}

            {/* Notification counts */}
            {notificationUnreadCount > 0 && (
              <div className="message-count-item">
                <Icon name="bell" color="orange" />
                <span>{notificationUnreadCount} system notification{notificationUnreadCount !== 1 ? 's' : ''}</span>
                <Label size="mini" color="orange" basic style={{ marginLeft: '6px', padding: '2px 6px' }}>
                  System
                </Label>
              </div>
            )}
          </>
        ) : (
          <div className="no-messages">
            <Icon name="check circle" color="green" />
            <span>No unread messages or notifications</span>
          </div>
        )}
        <div className="connection-status">
          <Icon
            name={isConnected ? 'wifi' : 'signal'}
            color={isConnected ? 'green' : 'red'}
          />
          <small>{connectionStatus}</small>
        </div>
      </div>
      <div className="popup-footer">
        <Button
          size="mini"
          primary
          onClick={handleIconClick}
          content="Open Messages"
        />
      </div>
    </div>
  );

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div className={`universal-message-icon ${getPositionClasses()} ${className}`}>
        <Popup
          trigger={
            <div className="message-icon-container">
              <Icon
                name="comments"
                style={getIconStyle()}
                className={`message-icon ${hasNewMessages ? 'has-messages' : ''} ${!isConnected ? 'disconnected' : ''}`}
                onClick={handleIconClick}
              />
              {combinedUnreadCount > 0 && (
                <Label
                  circular
                  color={getBadgeColor()}
                  size="mini"
                  className="message-badge"
                  content={combinedUnreadCount > 99 ? '99+' : combinedUnreadCount}
                />
              )}
              {!isConnected && (
                <Icon
                  name="warning sign"
                  color="orange"
                  size="small"
                  className="connection-warning"
                />
              )}
            </div>
          }
          content={getPopupContent()}
          position="bottom right"
          hoverable
          hideOnScroll
        />
      </div>

      {/* Message Center Modal */}
      <MessageCenter
        isOpen={isMessageCenterOpen}
        onClose={handleMessageCenterClose}
        userRole={userRole}
        showBusinessMessages={showBusinessNotifications}
      />
    </>
  );
};

export default UniversalMessageIcon;
